import { create } from "zustand"

/**
 * @typedef {Object} Presence.Tab
 * @property {string} page - The page pathname (e.g., '/broadcasts')
 * @property {number} timestamp - When the tab was opened
 * @property {string} status - Tab status (e.g., 'active')
 * @property {number} lastSeen - Last activity timestamp
 * @property {string} title - Document title
 * @property {boolean} isActive - Whether the tab is currently visible/focused
 */

/**
 * @typedef {Object} Presence.User
 * @property {number} id - User ID
 * @property {string} name - User name
 * @property {string} email - User email
 * @property {string} avatarUrl - User avatar URL
 * @property {number} accountId - Account ID
 * @property {Object.<string, Presence.Tab>} tabs - Object mapping session IDs to tab information
 */

/**
 * @typedef {Object} Presence.UserOnPage
 * @property {number} id - User ID
 * @property {string} name - User name
 * @property {string} email - User email
 * @property {string} avatarUrl - User avatar URL
 * @property {number} accountId - Account ID
 * @property {Object.<string, Presence.Tab>} tabs - Object mapping session IDs to tab information
 * @property {string} sessionId - The session ID for this specific tab
 * @property {Presence.Tab} tabInfo - The tab information for this specific tab
 */

/**
 * @typedef {Object} PresenceState
 * @property {Presence.User[]} onlineUsers - Array of online users with their tabs
 * @property {(onlineUsers: Presence.User[]) => void} setOnlineUsers - Function to update online users
 */

const usePresenceStore = create(set => ({
  onlineUsers: [],
  setOnlineUsers: onlineUsers => set({ onlineUsers })
}))

/**
 * Sets the online users in the store
 * @param {Presence.User[]} onlineUsers - Array of online users with their tabs
 */
export const setOnlineUsers = onlineUsers => {
  usePresenceStore.setState({ onlineUsers })
}

/**
 * Hook to get all online users
 * @returns {Presence.User[]} Array of online users with their tabs
 */
export const useOnlineUsers = () => {
  return usePresenceStore(state => state.onlineUsers)
}

/**
 * Hook to get online users on a specific page
 * @param {string} page - The page pathname to filter users by
 * @returns {Presence.UserOnPage[]} Array of users on the specified page with tab info
 */
export const useOnlineUsersOnPage = page => {
  return usePresenceStore(state => {
    if (!page) return []

    const usersOnPage = []

    state.onlineUsers.forEach(user => {
      Object.entries(user.tabs).forEach(([sessionId, tab]) => {
        if (tab.page === page) {
          // Create a user entry for each tab on this page
          usersOnPage.push({
            ...user,
            sessionId,
            tabInfo: tab
          })
        }
      })
    })

    return usersOnPage
  })
}
